import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { Printer, Package, Book, DollarSign, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import ServicesSection from "../../components/Services";
import bannerImg from "../../assets/Firefly 20250214075131.png"; // Add your banner image path here
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import { useEffect } from "react";

export default function CatalogPrinting() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const advantages = [
    {
      icon: <DollarSign className="w-12 h-12 text-primary-base" />,
      title: "Low-Cost Marketing Tool",
      description: "Catalog printing is an affordable way to showcase your products and attract potential customers without breaking the bank."
    },
    {
      icon: <Package className="w-12 h-12 text-primary-base" />,
      title: "Brand Building",
      description: "Catalogs give you an opportunity to build your brand identity and establish a strong presence in the market."
    },
    {
      icon: <Book className="w-12 h-12 text-primary-base" />,
      title: "Effective Advertising",
      description: "A well-designed catalog is a great advertising tool, promoting your products directly to your target audience."
    },
    {
      icon: <ArrowRight className="w-12 h-12 text-primary-base" />,
      title: "Increases Sales",
      description: "High-quality catalogs increase customer interest and drive them to your store, resulting in better sales conversion."
    }
  ];

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Catalog Printing
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              Catalog printing continues to play a vital role in promoting your business, even in this digital age. A printed catalog draws customers in, providing them with the details they need to make purchasing decisions.
            </p>
            <Link to="/contact">
            <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Get Started Today
            </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What is Catalog Printing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              What is Catalog Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Catalog printing refers to the creation of printed documents that showcase the products, services, and prices of your business. It serves as a powerful tool for driving customers to your store, enhancing their purchasing experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Advantages of Catalog Printing */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Advantages of Catalog Printing
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <div className="mb-6">{advantage.icon}</div>
                <h3 className="text-2xl font-bold text-primary-base mb-4">{advantage.title}</h3>
                <p className="text-gray-600">{advantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Catalog Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Our Catalog Printing Services
          </h2>
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We specialize in various types of catalog printing to suit different industries and business needs. Whether you're in retail, fashion, or corporate, our catalogs are designed to showcase your products effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Commercial Catalogs</h3>
              <p className="text-gray-600">
                Our commercial catalogs are perfect for businesses looking to promote their products in a cost-effective yet professional manner. We use the latest technology and high-quality materials for lasting impact.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Dress Material Catalogs</h3>
              <p className="text-gray-600">
                Promote your dress material collection with beautifully printed catalogs that feature vibrant images and detailed descriptions to attract potential buyers.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Customized Catalogs</h3>
              <p className="text-gray-600">
                We offer customized catalogs that are tailored to your specific business needs, helping you create a unique marketing tool that stands out from the competition.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Section */}
      {/* <section className="py-20 bg-neutral-light">
        <div className="text-center">
          <div className="relative">
            <img
              src={bannerImg}
              alt="Catalog Printing Example"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section> */}

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Get Your Catalog Printed Today!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Ready to promote your business with a high-quality catalog? Reach out today to get your catalog printing started and see the difference it can make for your sales!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>

      <ServicesSection />

      {/* Contact Section */}
      <ContactSection />
      <Footer />
    </div>
  );
}
